import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Film, Globe, Lock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const COLORS = ["#E11D48", "#F59E0B", "#10B981", "#3B82F6", "#8B5CF6", "#EC4899", "#14B8A6", "#F97316"];

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", color: "#E11D48", parent_id: "", public: true });

  const load = async () => {
    const r = await api.get("/groups");
    setGroups(r.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", description: "", color: COLORS[Math.floor(Math.random() * COLORS.length)], parent_id: "", public: true });
    setOpen(true);
  };
  const openEdit = (g) => {
    setEditing(g);
    setForm({ name: g.name, description: g.description || "", color: g.color || "#E11D48", parent_id: g.parent_id || "", public: g.public !== false });
    setOpen(true);
  };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    const body = { ...form, parent_id: form.parent_id || null };
    try {
      if (editing) {
        await api.patch(`/groups/${editing.id}`, body);
        toast.success("Group updated");
      } else {
        await api.post("/groups", body);
        toast.success("Group created");
      }
      setOpen(false);
      load();
    } catch { toast.error("Failed"); }
  };
  const remove = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"? Movies keep existing but lose this group.`)) return;
    try {
      await api.delete(`/groups/${g.id}`);
      toast.success("Group deleted");
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-[#E11D48] font-bold mb-2">Collections</p>
          <h1 className="font-display text-4xl font-bold tracking-tighter">Groups</h1>
          <p className="text-slate-400 text-sm mt-2 max-w-lg">Marvel → MCU → Infinity Saga. Disney. Studio Ghibli. DreamWorks. Organize your universe.</p>
        </div>
        <Button onClick={openNew} data-testid="new-group-btn" className="bg-[#E11D48] hover:bg-rose-700 rounded-full">
          <Plus className="w-4 h-4 mr-1" /> New group
        </Button>
      </div>

      {groups.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-white/10 rounded-2xl" data-testid="groups-empty">
          <Film className="w-10 h-10 mx-auto text-slate-600 mb-4" />
          <p className="text-slate-400 mb-4">No groups yet.</p>
          <Button onClick={openNew} className="bg-[#E11D48] hover:bg-rose-700 rounded-full">Create your first group</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="groups-grid">
          {groups.map(g => {
            const parent = g.parent_id ? groups.find(x => x.id === g.parent_id) : null;
            return (
              <div
                key={g.id}
                className="p-6 bg-[#0F0F12] rounded-xl border border-white/5 hover:border-white/20 transition-all group"
                data-testid={`group-card-${g.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: g.color + "22", color: g.color }}>
                    <Film className="w-5 h-5" />
                  </div>
                  <div className="flex items-center gap-1">
                    {g.public === false && (
                      <span title="Private — won't appear on your public profile" data-testid={`group-private-${g.id}`}>
                        <Lock className="w-3.5 h-3.5 text-slate-500" />
                      </span>
                    )}
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openEdit(g)} data-testid={`edit-group-${g.id}`} className="p-1.5 rounded hover:bg-white/10 text-slate-400 hover:text-white"><Pencil className="w-4 h-4" /></button>
                      <button onClick={() => remove(g)} data-testid={`delete-group-${g.id}`} className="p-1.5 rounded hover:bg-white/10 text-rose-400"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
                <div className="font-display text-xl font-semibold">{g.name}</div>
                {parent && <div className="text-xs text-slate-500 mt-0.5">↳ inside {parent.name}</div>}
                {g.description && <div className="text-sm text-slate-400 mt-2 line-clamp-2">{g.description}</div>}
                <div className="text-xs text-slate-500 mt-4 flex items-center gap-3">
                  <span><span className="text-white font-semibold">{g.movie_count || 0}</span> movies</span>
                  <span><span className="text-[#10B981] font-semibold">{g.watched_count || 0}</span> watched</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F0F12] border-white/10 text-slate-100" data-testid="group-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Edit group" : "New group"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Name</div>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="MCU" data-testid="group-name-input" className="bg-[#1A1A1E] border-white/10 focus-visible:ring-[#E11D48] text-white" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Description</div>
              <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Optional" data-testid="group-desc-input" className="bg-[#1A1A1E] border-white/10 focus-visible:ring-[#E11D48] text-white" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Parent group (optional)</div>
              <select
                value={form.parent_id}
                onChange={e => setForm({ ...form, parent_id: e.target.value })}
                data-testid="group-parent-select"
                className="w-full bg-[#1A1A1E] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus:ring-1 focus:ring-[#E11D48] focus:outline-none"
              >
                <option value="">— None (top-level) —</option>
                {groups.filter(x => !editing || x.id !== editing.id).map(x => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400 mb-2">Color</div>
              <div className="flex gap-2 flex-wrap">
                {COLORS.map(c => (
                  <button
                    key={c}
                    onClick={() => setForm({ ...form, color: c })}
                    data-testid={`color-${c}`}
                    className={`w-8 h-8 rounded-full transition-all ${form.color === c ? "ring-2 ring-white ring-offset-2 ring-offset-[#0F0F12]" : ""}`}
                    style={{ background: c }}
                  />
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-[#1A1A1E] border border-white/5">
              <div>
                <div className="text-sm font-medium flex items-center gap-2">
                  {form.public ? <Globe className="w-4 h-4 text-[#10B981]" /> : <Lock className="w-4 h-4 text-slate-500" />}
                  {form.public ? "Public group" : "Private group"}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{form.public ? "Visible on your public profile" : "Hidden — only you can see it"}</div>
              </div>
              <Switch
                checked={form.public}
                onCheckedChange={(v) => setForm({ ...form, public: v })}
                data-testid="group-public-toggle" />
            </div>
            <Button onClick={save} data-testid="save-group-btn" className="w-full bg-[#E11D48] hover:bg-rose-700 h-11">
              {editing ? "Save changes" : "Create group"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
